import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import shortid from 'shortid';
import { INTERNATIONAL_SUPPORT } from '@rsos/capstone/src/constants/capabilities';
import Loader from '@rsos/components/capstone/Loader';
import { validatedPayload } from '@rsos/ellington';
import fetchEmergencySession from '../../../actions/fetchEmergencySession';
import { getProductToken } from '../../../utils/getProductToken';
import { EllingtonCard } from '../../AdditionalData/EllingtonCard/EllingtonCard';
import ErrorBoundary from '../../AdditionalData/EllingtonCard/ErrorBoundary';
import { RADEError } from '../../AdditionalData/RADEErrors';
import { normalizeInlineRADE } from '../../AdditionalData/normalizeRADE';
import {
  CenteredBox,
  SearchPanel,
  SearchHeader,
  SearchBody,
} from './RADECard.styles';

export const RADECard = React.memo(
  ({ loading, error, data, accessToken, onFetchDataLink }) => {
    if (!data || data.length === 0) return null;
    return (
      <SearchPanel>
        <SearchHeader />
        {loading ? (
          <SearchBody>
            <CenteredBox>
              <Loader />
            </CenteredBox>
          </SearchBody>
        ) : error ? (
          <SearchBody>
            <CenteredBox>
              <RADEError />
            </CenteredBox>
          </SearchBody>
        ) : (
          <>
            <ErrorBoundary>
              {data.map(child => {
                const validated = validatedPayload({
                  data: child.data,
                  display_schema: child.display_schema,
                });
                return (
                  <EllingtonCard
                    key={shortid.generate()}
                    data={validated.data}
                    display_schema={validated.display_schema}
                    createdTime={Date.now()}
                    onFetchDataLink={onFetchDataLink}
                    showDisclaimer
                    source={child.org}
                    showExitButton
                    accessToken={accessToken}
                  />
                );
              })}
            </ErrorBoundary>
          </>
        )}
      </SearchPanel>
    );
  },
);

const ConnectedRADECard = ({ setMafError, setADRError }) => {
  const dispatch = useDispatch();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const showing = useSelector(state => state.rade.showing);
  const triggerSearch = useSelector(state => state.rade.triggerSearch);
  const searchTerm = useSelector(state => state.search.searchTerm);
  const { productToken = '' } = useSelector(state =>
    getProductToken(state.sinatra),
  );

  const isInternational = useSelector(
    state =>
      !!(
        state.psaps.currentPSAP.activeCapabilities &&
        state.psaps.currentPSAP.activeCapabilities[INTERNATIONAL_SUPPORT]
      ),
  );

  const handleFetchDataLink = useCallback(
    props => {
      const { mafid, schema = {} } = props;
      return new Promise((resolve, reject) => {
        (async () => {
          const { attributes = {} } = schema;
          try {
            const { payload } = await dispatch(
              fetchEmergencySession(mafid, {
                attributes,
                silentUpdate: true,
              }),
            );
            resolve(normalizeInlineRADE(payload));
          } catch (action) {
            reject(action.error || action);
          }
        })();
      });
    },
    [dispatch],
  );

  useEffect(() => {
    if (showing) {
      (async () => {
        setError(null);
        setLoading(true);
        let scheme;
        try {
          if (!searchTerm) {
            return;
          }
          let normalizedSearchTerm = searchTerm.replace(/\/|\s|-/g, ''); //filters forwardSlashes, dashes, and spaces
          if (searchTerm.includes('/maf')) {
            scheme = 'maf-id';
            normalizedSearchTerm = normalizedSearchTerm.replace('maf', '');
          } else if (!searchTerm.includes('///')) {
            scheme = 'tel';
            if (!isInternational) {
              if (normalizedSearchTerm.substring(0, 1) !== '1') {
                normalizedSearchTerm = `1${normalizedSearchTerm}`;
              }
            }
          }
          const resp = await dispatch(
            fetchEmergencySession(normalizedSearchTerm, {
              attributes: { scheme },
            }),
          );
          const normalized = normalizeInlineRADE(resp.payload);

          if (!normalized.length) {
            if (scheme === 'maf-id') {
              setMafError(true);
            }
            if (scheme === 'tel') {
              setADRError(true);
            }
          }
          setData(normalized);
        } catch (error) {
          if (scheme === 'tel') {
            setADRError(true);
          } else {
            setError(error);
          }
        } finally {
          setLoading(false);
        }
      })();
    }
    return () => {
      setData(null);
      setError(null);
      setLoading(false);
    };
    // NOTE Ignoring searchTerm in deps, so it doesn't retrigger on every key change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, showing, triggerSearch]);

  return (
    showing && (
      <RADECard
        data={data}
        error={error}
        loading={loading}
        onFetchDataLink={handleFetchDataLink}
        accessToken={productToken}
      />
    )
  );
};

export default ConnectedRADECard;
